import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { network, type NetworkOffer } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { Store, ChevronDown, Users, TrendingUp, Plus, ArrowRight, CheckCircle } from "lucide-react";

function OfferCard({ offer, onPartner, joining }: {
  offer: NetworkOffer; onPartner: () => void; joining: boolean;
}) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5 hover:shadow-md hover:shadow-black/[0.04] transition-shadow flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {offer.industry && (
              <span className="px-2.5 py-0.5 rounded-full bg-black/[0.04] text-xs font-semibold text-slate capitalize">
                {offer.industry}
              </span>
            )}
            <span className="px-2.5 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-bold">
              {offer.commission_pct}% commission
            </span>
          </div>
          <p className="font-bold text-ink leading-snug">{offer.title}</p>
          <p className="text-xs text-slate mt-1">
            by {offer.merchant_company || offer.merchant_name}
          </p>
        </div>
      </div>

      {offer.description && (
        <p className="text-sm text-slate leading-relaxed line-clamp-2 flex-1">{offer.description}</p>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs text-slate">
          {offer.min_audience > 0 && (
            <span className="flex items-center gap-1">
              <Users size={11} /> {offer.min_audience.toLocaleString()}+ subscribers
            </span>
          )}
          {offer.partner_count !== undefined && (
            <span className="flex items-center gap-1">
              <TrendingUp size={11} /> {offer.partner_count} partners
            </span>
          )}
        </div>
        <button onClick={onPartner} disabled={joining}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-coral text-white text-xs font-semibold hover:bg-coral-dark disabled:opacity-60 transition-colors shadow-sm shadow-coral/20">
          {joining ? "Joining…" : <><Plus size={12} /> Partner Up</>}
        </button>
      </div>
    </div>
  );
}

export default function MarketplacePage() {
  const [offers, setOffers]       = useState<NetworkOffer[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadMore] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const [successId, setSuccessId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { const r = await network.marketplace({ maxResults: 20 }); setOffers(r.offers); setNextToken(r.nextToken); }
    catch (e: any) { setError(e.message || "Failed to load marketplace"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadMore(true);
    try {
      const r = await network.marketplace({ maxResults: 20, nextToken });
      setOffers((p) => [...p, ...r.offers]); setNextToken(r.nextToken);
    } finally { setLoadMore(false); }
  };

  const handlePartner = async (offer: NetworkOffer) => {
    setJoiningId(offer.id); setError(null);
    try {
      await network.joinOffer(offer.id);
      setSuccessId(offer.id);
      setTimeout(() => setSuccessId(null), 4000);
    } catch (e: any) { setError(e.message || "Failed to join offer"); }
    finally { setJoiningId(null); }
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink">Marketplace</h1>
          <p className="text-xs sm:text-sm text-slate mt-0.5">Partner with other merchants to earn commissions</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/network/my-offers"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/[0.14] hover:text-ink transition-colors">
            My offers <ArrowRight size={14} />
          </Link>
          <Link to="/network/earnings"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/[0.14] hover:text-ink transition-colors">
            <TrendingUp size={14} />
            <span className="hidden sm:inline">Earnings</span>
          </Link>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5">
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
        )}
        {successId && (
          <div className="flex items-center gap-2 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
            <CheckCircle size={16} /> You've joined this offer! Your tracking link is now in{" "}
            <Link to="/network/earnings" className="font-semibold hover:underline">Earnings</Link>.
          </div>
        )}

        {loading ? <Spinner className="py-20" /> : offers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-16 text-center">
            <Store size={32} className="text-slate opacity-20 mx-auto mb-4" />
            <p className="font-bold text-ink mb-1">No offers available yet</p>
            <p className="text-sm text-slate mb-6 max-w-xs mx-auto">
              Be the first to create an offer and let other merchants promote your product.
            </p>
            <Link to="/network/my-offers"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
              <Plus size={15} /> Create an offer
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {offers.map((o) => (
                <OfferCard key={o.id} offer={o}
                  onPartner={() => handlePartner(o)}
                  joining={joiningId === o.id}
                />
              ))}
            </div>
            {nextToken && (
              <div className="flex justify-center">
                <button onClick={loadMore} disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/[0.14] disabled:opacity-60">
                  <ChevronDown size={15} />
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
